import {
    LoanApplication,
    DiscountApplicationMethod,
    DiscountType,
    ComissionDiscount,
    InterestRateDiscount,
    GracePeriodDiscount,
    AdditiveDateApplicationMethod
} from 'generated/graphql';
import { IDiscountCalculated } from 'interfaces/interfaces';
import moment from 'moment';

export const hasDiscountsToApply = (discounts: any[], loanApplication: LoanApplication): boolean =>
    !loanApplication.hasRenegotiation || discounts.find(d => d.allowsRenegotiation);

export const hasComissionDiscount = (discounts: any[], loanApplication: LoanApplication): boolean => {
    const comissionDiscounts = discounts.filter(d => d.__typename === "ComissionDiscount");
    return comissionDiscounts.length > 0 && hasDiscountsToApply(comissionDiscounts, loanApplication);
}
export const hasInterestRateDiscount = (discounts: any[], loanApplication: LoanApplication): boolean => {
    const interestRateDiscounts = discounts.filter(d => d.__typename === "InterestRateDiscount");
    return interestRateDiscounts.length > 0 && hasDiscountsToApply(interestRateDiscounts, loanApplication);
}
export const hasGracePeriodDiscount = (discounts: any[], loanApplication: LoanApplication): boolean => {
    const gracePeriodDiscounts = discounts.filter(d => d.__typename === "GracePeriodDiscount");
    return gracePeriodDiscounts.length > 0 && hasDiscountsToApply(gracePeriodDiscounts, loanApplication);
}

export const calculateDiscounts = (discounts: DiscountType[], loanApplication: LoanApplication): IDiscountCalculated => {
    let discountCalculated:IDiscountCalculated = {};

    const interestRateDiscount = {} as InterestRateDiscount;
    const gracePeriodDiscount = {} as GracePeriodDiscount;

    discounts.forEach(discount => {
        if (interestRateDiscount && interestRateDiscount.value !== undefined) {
            discountCalculated = calculateInterestRateDiscount(discountCalculated, discount, loanApplication);
        } else if (gracePeriodDiscount && gracePeriodDiscount.value !== undefined) {
            discountCalculated = calculateGracePeriodDiscount(discountCalculated, discount, loanApplication);
        }
    });

    return discountCalculated;
}

export const calculateComissionDiscount = (discount: DiscountType, comission: number, hasRenegotiation: boolean): number => {
    const comissionDiscount = discount as ComissionDiscount;
    let calculatedComission = comission;

    if (!hasRenegotiation || comissionDiscount.allowsRenegotiation) {
        switch (comissionDiscount.applicationMethod) {
            case DiscountApplicationMethod.Absolute:
                calculatedComission = comission - comissionDiscount.value;
                break;
            case DiscountApplicationMethod.Proportional:
                calculatedComission = comission - (comission * (comissionDiscount.value / 100));
                break;
            default:
                break;
        }
    }

    return Math.max(calculatedComission, 0);
}

const calculateInterestRateDiscount = (
    discountCalculated: IDiscountCalculated,
    discount: DiscountType,
    loanApplication: LoanApplication
): IDiscountCalculated => {
    const interestRateDiscount = discount as InterestRateDiscount;
    const proposal = loanApplication.acceptedProposal;

    if (!loanApplication.hasRenegotiation || interestRateDiscount.allowsRenegotiation) {
        switch (interestRateDiscount.applicationMethod) {
            case DiscountApplicationMethod.Absolute:
                discountCalculated.calculatedInterestRate = proposal.interestRate - interestRateDiscount.value;
                break;
            case DiscountApplicationMethod.Proportional:
                discountCalculated.calculatedInterestRate =
                    proposal.interestRate - (proposal.interestRate * (interestRateDiscount.value / 100));
                break;
            default:
                break;
        }
    }

    discountCalculated.calculatedInterestRate = Math.max(discountCalculated.calculatedInterestRate, 1.99);

    return discountCalculated;
}

const calculateGracePeriodDiscount = (
    discountCalculated: IDiscountCalculated,
    discount: DiscountType,
    loanApplication: LoanApplication
): IDiscountCalculated => {
    const gracePeriodDiscount = discount as GracePeriodDiscount;

    if (!loanApplication.hasRenegotiation || gracePeriodDiscount.allowsRenegotiation) {

        switch (gracePeriodDiscount.applicationMethod) {
            case DiscountApplicationMethod.Absolute:
                discountCalculated.calculatedPaymentDay = gracePeriodDiscount.fixedDate;
                break;
            case DiscountApplicationMethod.Additive:
                const { additiveDate } = gracePeriodDiscount;
                const defaultLoanDate = moment().startOf('day').add(5, 'd');

                switch (additiveDate.applicationMethod) {
                    case AdditiveDateApplicationMethod.Day:
                        discountCalculated.calculatedPaymentDay = defaultLoanDate.add(additiveDate.amount, 'd').toString();
                        break;
                    case AdditiveDateApplicationMethod.Month:
                        discountCalculated.calculatedPaymentDay = defaultLoanDate.add(additiveDate.amount, 'M').toString();
                        break;
                    default:
                        break;
                }
                break;
            default:
                break;
        }
    }


    return discountCalculated;
}
